import { SceneManager } from "../../common/scene.manager";
import { BaseBabylonService } from "./base-babylon.service";
import { HybridBabylonService } from "./Hybrid/hybrid-babylon.service";
import { Building, ContextItem, SiteOptions } from "../../confi-state";
import { buildingType } from "./models/building-type.enum";
import { BuildingHelperService } from "../../app-confi/services/building-helper.service";
import { LonLatToTiles } from "../../common/lon-lat-to-tiles";
import { MapService } from "../../common/services/map.service";
import { UtilityService } from "../../common/services/utility.service";
import { accuracyEnum } from "../../common/models/accuracy.enum";
import { PropsHybrid } from "./Hybrid/models/props-config";
import { CorridorBabylonService } from "./Corridor/corridor-babylon.service";
import { PropsCorridor } from "./Corridor/models/props-config";
import { TowerBabylonService } from "./Tower/tower-babylon.service";
import { PropsTower } from "./Tower/models/props-config";
import { Part } from "./models/part";
import { RowhouseBabylonService } from "./Rowhouse/rowhouse-babylon.service";
import { PropsRowhouse } from "./Rowhouse/models/props-config";
import { environment } from "../../environment.enum";
import { getEnvKey } from "../../environments";
import { nlCartiqoLayersEnum } from "../../common/models/nl-cartiqo-layers.enum";
import { inflateCoordinatesArray } from "ol/geom/flat/inflate";
import { BuildingFeature } from "../../models/building-feature";
import { TreeFeature } from "../../models/tree-feature";
import { Polygon } from "../../common/models/polygon";
import { Features } from "../../models/features";
import { ParcelFeature } from "../../models/parcel-feature";
import { partTypeEnum as partTypeTowerEnum } from "./Tower/models/part-type.enum";
import { Column } from "./models/column";
import { columnDefs as corridorColumnDefs } from "./Corridor/models/column-definition";
import { columnDefs as hybridColumnDefs } from "./Hybrid/models/column-definition";
import { columnDefs as rowhouseColumnDefs } from "./Rowhouse/models/column-definition";
import { columnDefs as towerColumnDefs } from "./Tower/models/column-definition";
import { Point2, Point3 } from "../../common/models/point.type";


interface SitePolygonData {
    type: nlCartiqoLayersEnum, coordinates: Point2[][][], id: string, featureId: number, parcel: number, height: number, offsetFromGround: number, color: Point3, visible: boolean, alpha: number,
    edges: { width: number, color: Point3 }, data: any
};
interface BuildingPolygonData { type: nlCartiqoLayersEnum, coordinates: Point2[][][], id: string, featureId: number, height: number, offsetFromGround: number, color: Point3, visible: boolean, data: any }
interface TreePolygonData { type: nlCartiqoLayersEnum, coordinate: Point3, id: string, visible: boolean, data: any };

export class BabylonService extends BaseBabylonService {

    private pathAssets = '/assets/assets/';

    longLat = new LonLatToTiles();

    buildingHelperService: BuildingHelperService;
    hybridBabylonService: HybridBabylonService;
    corridorBabylonService: CorridorBabylonService;
    towerBabylonService: TowerBabylonService;
    rowhouseBabylonService: RowhouseBabylonService;

    mapService: MapService;
    utilityService: UtilityService;

    constructor(sceneManager: SceneManager, buildingHelperService: BuildingHelperService, mapService: MapService, utilityService: UtilityService) {
        super(sceneManager);
        this.buildingHelperService = buildingHelperService;
        this.hybridBabylonService = new HybridBabylonService(sceneManager, buildingHelperService);
        this.corridorBabylonService = new CorridorBabylonService(sceneManager, buildingHelperService);
        this.towerBabylonService = new TowerBabylonService(sceneManager, buildingHelperService);
        this.rowhouseBabylonService = new RowhouseBabylonService(sceneManager, buildingHelperService);
        this.mapService = mapService;
        this.utilityService = utilityService;
    }

    convertPartsToColumns<T>(parts: Part<T, string>[]): T[] {
        return parts.map(s => s.columns).flat();
    }

    loadSite(lon: number, lat: number, selectedParcels: ParcelFeature[], siteContextItems: ContextItem[], siteOptions: SiteOptions): Promise<{ features: Features }> {
        return new Promise((resolve, _) => {

            const zoom = 19;
            const subdivisions = {
                h: 10,
                w: 10
            };
            const xTileBase = this.longLat.long2tile(lon, zoom) - subdivisions.w / 2;
            const yTileBase = this.longLat.lat2tile(lat, zoom) + subdivisions.h / 2;

            this.sceneManager.contextService.cleanExtrusion();
            this.sceneManager.cleanTiledGround();

            const mapInitiallyVisible = siteOptions ? siteOptions.mapVisible : true;
            const gridInitiallyVisible = siteOptions ? siteOptions.gridVisible : true;

            setTimeout(() => {
                this.sceneManager.tileService.updateTiles(subdivisions, zoom, xTileBase, yTileBase, lat, mapInitiallyVisible);
                if (!gridInitiallyVisible) {
                    this.sceneManager.hideGroundGrid();
                }
                this.mapService.loadFeaturesOfMapExtent(
                    `https://api.maptiler.com/tiles/${getEnvKey((process.env.REACT_APP_ENVIRONMENT as environment), 'maptilerTilesKey')}/{z}/{x}/{y}.pbf?key=${getEnvKey((process.env.REACT_APP_ENVIRONMENT as environment), 'maptilerKey')}`,
                    16,
                    this.longLat.tile2long(xTileBase + (subdivisions.w / 2), zoom),
                    this.longLat.tile2lat(yTileBase - ((subdivisions.h / 2) - 1), zoom),
                ).then((result) => {
                    const features: Features = this.extractFeaturesAndCreateContextItems(result, siteContextItems, selectedParcels, siteOptions);
                    resolve({ features });
                });
            }, 50);

        })
    }

    private extractFeaturesAndCreateContextItems(mapdata, siteContextItems: ContextItem[], selectedParcels: ParcelFeature[], siteOptions: SiteOptions) {
        let buildingPolygons: BuildingPolygonData[] = [];
        let sitePolygons: SitePolygonData[] = [];
        let trees: TreePolygonData[] = [];

        const zeroCoordinate = mapdata.map.getCoordinateFromPixel([300, 300]);
        // TODO research how to derive this scale more precisely, now it's a bit of a guess work
        const scale = 0.615;

        const features: Features = {
            buildings: [],
            parcels: [],
            trees: [],
            zones: [],
            dimensions: [],
        };

        mapdata.features.forEach((feature) => {
            const featureProps = feature.getProperties();
            if (featureProps.layer === nlCartiqoLayersEnum.buildings) {
                this.handleBuildingFeature(feature, zeroCoordinate, scale, featureProps, buildingPolygons, siteContextItems, siteOptions, features);
            } else if (featureProps.layer === nlCartiqoLayersEnum.trees) {
                this.handleTreesFeature(feature, zeroCoordinate, scale, featureProps, siteContextItems, trees, siteOptions, features);
            } else if (featureProps.layer === nlCartiqoLayersEnum.parcels) {
                this.handleParcelsFeature(feature, zeroCoordinate, scale, featureProps, sitePolygons, selectedParcels, features);
            } else if (featureProps.layer === nlCartiqoLayersEnum.zones) {
                // TODO need to extract polygons here
                features.zones.push({ id: featureProps.originalid, featureId: feature.id_, props: featureProps });
            } else if (featureProps.layer === nlCartiqoLayersEnum.dimensions) {
                features.dimensions.push({ id: featureProps.originalid, featureId: feature.id_, props: featureProps });
            }
        });

        this.create3DContextItems(buildingPolygons, features, trees, sitePolygons);
        return features;
    }

    private create3DContextItems(buildingPolygons: BuildingPolygonData[], features: Features, trees: TreePolygonData[], sitePolygons: SitePolygonData[]) {
        buildingPolygons.forEach(buildingPolygon => {
            const polygons = this.createExtrusions(buildingPolygon);
            const f = features.buildings.find(b => b.id === buildingPolygon.id);
            f.polygons = polygons;
        });

        trees.forEach(tree => {
            this.sceneManager.contextService.createInstanceContextItem<TreeFeature>(this.pathAssets, 'tree.glb', tree);
        });

        sitePolygons.forEach(sitePolygon => {
            const polygons = this.createExtrusions(sitePolygon);
            const f = features.parcels.find(b => b.id === sitePolygon.id);
            f.polygons = polygons;
        });
    }

    private handleParcelsFeature(feature: any, zeroCoordinate: any, scale: number, featureProps: any, sitePolygons: SitePolygonData[], selectedParcels: ParcelFeature[], features: Features) {
        const inflatedCoordinates = this.getInflatedAndAdjustedCoordinates(feature, zeroCoordinate, scale);
        const id = featureProps.originalid;
        const parcel = featureProps.parcel;
        const featureId = feature.id_;

        const type = nlCartiqoLayersEnum.parcels;

        const foundPolygon = sitePolygons.find(s => s.featureId === featureId);

        if (foundPolygon) {
            foundPolygon.coordinates.push(inflatedCoordinates.map(m => m.map(c => [c[1], c[0]])));
        } else {
            const spolygon: SitePolygonData = {
                type,
                coordinates: [],
                id,
                featureId,
                parcel,
                height: 0.1,
                offsetFromGround: 0.3,
                color: [0.1, 0.1, 0.8],
                visible: false,
                data: featureProps,
                edges: {
                    color: [0, 0, 1],
                    width: 50,
                },
                alpha: 1
            };
            if (selectedParcels && selectedParcels.some(p => p.originalid === spolygon.id)) {
                spolygon.visible = true;
                spolygon.alpha = 0;
            } else {
                spolygon.visible = false;
            }
            spolygon.coordinates.push(inflatedCoordinates.map(m => m.map(c => [c[1], c[0]])));
            sitePolygons.push(spolygon);
        }
        features.parcels.push({ id, featureId, props: featureProps });
    }

    private handleTreesFeature(feature: any, zeroCoordinate: any, scale: number, featureProps: any, siteContextItems: ContextItem[], trees: TreePolygonData[], siteOptions: SiteOptions, features: Features) {
        const fcoord = ((feature as any).flatCoordinates_ as Point2);
        const x = this.mapService.compensateAndScaleCoord(fcoord[0], zeroCoordinate[0], scale);
        const y = this.mapService.compensateAndScaleCoord(fcoord[1], zeroCoordinate[1], scale);
        const id = featureProps.originalid;
        const featureId = feature.id_;
        const type = nlCartiqoLayersEnum.trees;

        const treeItem = { type, coordinate: [y, 0, x], id, visible: (siteOptions ? siteOptions.treesVisible : true), data: featureProps };

        if (siteContextItems && (siteOptions ? siteOptions.treesVisible : true)) {
            const siteContextItem = siteContextItems.find(sci => sci.id === id);
            if (siteContextItem) {
                treeItem.visible = siteContextItem.visible;
            }
        }
        trees.push(treeItem as any);
        features.trees.push({ id, featureId, props: featureProps, pos: [y, x] });
    }

    private handleBuildingFeature(feature: any, zeroCoordinate: any, scale: number, featureProps: any, buildingPolygons: BuildingPolygonData[], siteContextItems: ContextItem[], siteOptions: SiteOptions, features: Features) {
        const inflatedCoordinates = this.getInflatedAndAdjustedCoordinates(feature, zeroCoordinate, scale);
        const id = featureProps.originalid;
        const featureId = feature.id_;
        const type = nlCartiqoLayersEnum.buildings;

        const foundPolygon = buildingPolygons.find(s => s.featureId === featureId);

        if (foundPolygon) {
            foundPolygon.coordinates.push(inflatedCoordinates.map(m => m.map(c => [c[1], c[0]] as Point2)));
        } else {
            const bpolygon = this.createBuildingPolygon(type, id, featureId, featureProps, inflatedCoordinates, siteContextItems, siteOptions);
            buildingPolygons.push(bpolygon);
        }
        features.buildings.push({ id, featureId, props: featureProps });
    }

    private createBuildingPolygon(type: nlCartiqoLayersEnum, id: any, featureId: number, featureProps: BuildingFeature, merge: Point2[][], siteContextItems: ContextItem[], siteOptions: SiteOptions) {
        const polygon = { type, coordinates: [], id, featureId, height: featureProps.height ? featureProps.height : 10, offsetFromGround: 0, color: featureProps.height ? [1, 1, 1] as Point3 : [1, 1, 1] as Point3, visible: (siteOptions ? siteOptions.buildingsVisible : true), data: featureProps };
        polygon.coordinates.push(merge.map(m => m.map(c => [c[1], c[0]])));
        if (siteContextItems && (siteOptions ? siteOptions.buildingsVisible : true)) {
            const siteContextItem = siteContextItems.find(sci => sci.id === id);
            if (siteContextItem) {
                polygon.visible = siteContextItem.visible;
            }
        }

        return polygon;
    }

    private getInflatedAndAdjustedCoordinates(feature: any, zeroCoordinate, scale: number) {
        const coords = feature.getFlatCoordinates();
        const ends = feature.getEnds()
        const inflatedCoordinates = inflateCoordinatesArray(coords, 0, ends, 2);
        const fcoord = inflatedCoordinates;
        const merge = fcoord.map(x => this.mapService.compensateAndScaleCoordinates(x, zeroCoordinate, scale) as Point2[]);
        return merge;
    }

    markBuildingsAsDeprecated(buildings: Building[]) {
        buildings.forEach(building => {
            if (!building.props || !building.props.parts) {
                building.deprecated = true;
            } else if (building.props && building.props.parts && building.props.parts.length > 0) {
                const columns = building.props.parts.map(p => p.columns).flat() as Column<any, any, any, any>[];
                let colTypeExistInDef;

                if (building.type === buildingType.singleLoadedCorridor || building.type === buildingType.hybrid) {
                    colTypeExistInDef = this.columnTypesExistInDefinitions(columns, hybridColumnDefs);
                } else if (building.type === buildingType.doubleLoadedCorridor) {
                    colTypeExistInDef = this.columnTypesExistInDefinitions(columns, corridorColumnDefs);
                } else if (building.type === buildingType.rowhouse) {
                    colTypeExistInDef = this.columnTypesExistInDefinitions(columns, rowhouseColumnDefs);
                } else if (building.type === buildingType.tower) {
                    colTypeExistInDef = this.columnTypesExistInDefinitions(columns, towerColumnDefs);
                }

                if (!colTypeExistInDef) {
                    building.deprecated = true;
                } else {
                    building.deprecated = false;
                }
            }
        });
    }

    columnTypesExistInDefinitions(columns: Column<any, any, any, any>[], columnDefs: { type: string }[]) {
        let colTypeExistInDef = true;
        for (let i = 0; i < columns.length; i++) {
            const col = columns[i];
            const foundColumn = columnDefs.find(cd => cd.type === col.type);
            if (!foundColumn) {
                colTypeExistInDef = false;
                break;
            }
        }
        return colTypeExistInDef;
    }

    mapPartsForBuildings(buildings: Building[]) {
        if (buildings && buildings.length > 0) {
            buildings.forEach(building => {
                if (building.props && building.props.parts && building.props.parts.length > 0 && !building.deprecated) {
                    switch (building.type) {
                        case buildingType.hybrid:
                            const ph = building.props as PropsHybrid;
                            if (ph.partsRefined && ph.partsRefined.length > 0) {
                                ph.partsRefined[0].parts = this.hybridBabylonService.mapPartsTo3D(ph.partsRefined[0].parts,
                                    ph.moosPlazaAlignment, true
                                )
                            }
                            if (ph.parts) {
                                ph.parts = this.hybridBabylonService.mapPartsTo3D(ph.parts,
                                    ph.moosPlazaAlignment,
                                )
                            }

                            break;
                        case buildingType.singleLoadedCorridor:
                            const ps = building.props as PropsHybrid;
                            if (ps.partsRefined && ps.partsRefined.length > 0) {
                                ps.partsRefined[0].parts = this.hybridBabylonService.mapPartsTo3D(ps.partsRefined[0].parts,
                                    ps.moosPlazaAlignment, true
                                )
                            }
                            if (ps.parts) {
                                ps.parts = this.hybridBabylonService.mapPartsTo3D(ps.parts,
                                    ps.moosPlazaAlignment,
                                )
                            }
                            break;
                        case buildingType.doubleLoadedCorridor:
                            let pc = building.props as PropsCorridor;
                            if (pc.partsRefined && pc.partsRefined.length > 0) {

                                pc.partsRefined[0].parts = this.corridorBabylonService.mapPartsTo3D(
                                    pc.partsRefined[0].parts,
                                    pc.moosPlazaAlignment,
                                    pc.moosPlazaPosition,
                                    true,
                                )
                            }
                            if (pc.parts) {
                                pc.parts = this.corridorBabylonService.mapPartsTo3D(
                                    pc.parts,
                                    pc.moosPlazaAlignment,
                                    pc.moosPlazaPosition,
                                )
                            }
                            break;
                        case buildingType.tower:
                            let pt = building.props as PropsTower;
                            if (pt.partsRefined && pt.partsRefined.length > 0) {

                                let selectedHead = pt.partsRefined[0].parts.find(p => p.type === partTypeTowerEnum.headLeft);
                                let selectedHeadType;
                                if (selectedHead && selectedHead.columns.length > 0) {
                                    const headCol = selectedHead.columns[0];
                                    selectedHeadType = headCol.type;
                                }

                                pt.partsRefined[0].parts = this.towerBabylonService.mapPartsTo3D(
                                    pt.partsRefined[0].parts,
                                    pt.moosPlazaAlignment,
                                    pt.moosPlazaPosition,
                                    true,
                                )
                            }
                            if (pt.parts) {
                                pt.parts = this.towerBabylonService.mapPartsTo3D(
                                    pt.parts,
                                    pt.moosPlazaAlignment,
                                    pt.moosPlazaPosition,
                                )
                            }
                            break;
                        case buildingType.rowhouse:
                            let pr = building.props as PropsRowhouse;
                            if (pr.partsRefined && pr.partsRefined.length > 0) {
                                pr.partsRefined[0].parts = this.rowhouseBabylonService.mapPartsTo3D(pr.partsRefined[0].parts,
                                    true
                                )
                            }
                            if (pr.parts) {
                                pr.parts = this.rowhouseBabylonService.mapPartsTo3D(
                                    pr.parts
                                )
                            }
                            break;
                        default:
                            break;
                    }
                }
            })
        }
    }

    renderBuildings(buildings: Building[], accuracy: accuracyEnum): Promise<any>[] {
        return buildings.map(building => {
            return this.renderBuildingTo3D(building, accuracy);
        });
    }

    renderBuildingTo3D(building: Building, accuracy: accuracyEnum): Promise<any> {
        if (!building.deprecated) {
            if (building.type === buildingType.hybrid || building.type === buildingType.singleLoadedCorridor) {
                const props = building.props as PropsHybrid;
                const parts = building.refined && props.partsRefined && props.partsRefined.length > 0 ? props.partsRefined[0].parts : props.parts;
                return this.hybridBabylonService.renderColumnsTo3D(
                    building.id,
                    parts,
                    props.facade,
                    building.locationInSite,
                    accuracy
                );
            } else if (building.type === buildingType.doubleLoadedCorridor) {
                const props = building.props as PropsCorridor;
                const parts = building.refined && props.partsRefined && props.partsRefined.length > 0 ? props.partsRefined[0].parts : props.parts;
                return this.corridorBabylonService.renderColumnsTo3D(
                    building.id,
                    parts,
                    building.locationInSite,
                    accuracy
                );
            } else if (building.type === buildingType.tower) {
                const props = building.props as PropsTower;
                const parts = building.refined && props.partsRefined && props.partsRefined.length > 0 ? props.partsRefined[0].parts : props.parts;
                return this.towerBabylonService.renderColumnsTo3D(
                    building.id,
                    parts,
                    building.locationInSite,
                    props.selectedCoreType,
                    accuracy
                );
            } else if (building.type === buildingType.rowhouse) {
                const props = building.props as PropsRowhouse;
                const parts = building.refined && props.partsRefined && props.partsRefined.length > 0 ? props.partsRefined[0].parts : props.parts;
                return this.rowhouseBabylonService.renderColumnsTo3D(
                    building.id,
                    parts,
                    building.locationInSite,
                    accuracy
                );
            }
        } else {
            return Promise.resolve();
        }
    }

    cleanScene() {
        this.sceneManager.modelLoaderService.cleanAllMeshInstances();
        this.sceneManager.modelLoaderService.removeAllGroupControls();

        if (this.sceneManager.imageTileService) {
            this.sceneManager.imageTileService.deleteImageTile();
        }
        this.sceneManager.cleanTiledGround();
        this.sceneManager.contextService.cleanExtrusion();
    }

    remove3DBuilding(buildingId: number | string) {
        this.sceneManager.modelLoaderService.cleanMeshInstancesWithGroupId(buildingId);
        this.sceneManager.modelLoaderService.removeGroupControl(buildingId);
    }

    private createExtrusions(polygonData: { type: nlCartiqoLayersEnum; coordinates: Point2[][][]; id: string; featureId: number, height: number; offsetFromGround: number; color: Point3; visible: boolean; data: any; }): Polygon[] {
        if (polygonData.coordinates.length > 1) {

            const pathsWithHoles = this.utilityService.getPathsAndHoles(polygonData.coordinates);
            const unioned = this.utilityService.unionPolygons(pathsWithHoles);

            unioned.forEach(u => {
                this.createPolygonExtrusion(u, polygonData);
            });
            return unioned;
        } else if (polygonData.coordinates.length === 1) {
            const p = this.utilityService.getPathsAndHoles(polygonData.coordinates);
            this.createPolygonExtrusion(p[0], polygonData);
            return p;
        }
    }

    private createPolygonExtrusion(polygon: Polygon, polygondata: { type: nlCartiqoLayersEnum; id: string; height: number; offsetFromGround: number; color: Point3; visible: boolean; data: any; }) {
        this.sceneManager.contextService.createPolygonExtrusionContextItem<BuildingFeature>({ ...polygondata, holes: polygon.holes, coordinates: polygon.path });
    }

}